import React, { Fragment, useState, useEffect } from "react";
import { Col, Card, CardBody, Form, FormGroup, Label, Row } from "reactstrap";
import { H3, Breadcrumbs } from "../../../AbstractElements";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Cancel, Save } from "../../../Constant";
import {
    storeShiftDetails,
    storeShiftDetailsRole,
    updateShift,
    updateShiftRole,
    getShiftDetails,
    getShiftDetailsRole
} from "../../../CommonUrl";
import { useSelector } from "react-redux";
import { postClientUrlWithToken, getClientUrlDataToken } from "../../../Utils/restUtils";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Transparentspinner from "../../Common/Loader/Transparentspinner";
import HandleApiError from "../../Common/ErrorImport/ErrorImport";
import Button from "../../Common/Elements/Button";

const schema = yup.object().shape({
    scheduleName: yup.string().required('Schedule Name is required').label('Schedule Name'),
    startTime: yup.string().required('Start Time is required').label('Start Time'),
    endTime: yup.string().required('End Time is required').label('End Time'),
    status: yup.mixed().required('Status is required').label('Status'),
});

const AddShift = () => {
    const [searchParams] = useSearchParams();
    const shiftId = searchParams.get("id");
    const isEdit = !!shiftId;

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            scheduleName: '',
            startTime: '',
            endTime: '',
            description: '',
            status: { value: "active", label: "Active" },
        },
    });

    const authState = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const convertTo24Hour = (time12) => {
        if (!time12) return "";
        if (/^\d{2}:\d{2}$/.test(time12)) return time12;
        const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return time12;
        let [_, hoursStr, minutesStr, ampm] = match;
        let hours = parseInt(hoursStr, 10);
        ampm = ampm.toUpperCase();
        if (ampm === "PM" && hours < 12) {
            hours += 12;
        } else if (ampm === "AM" && hours === 12) {
            hours = 0;
        }
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        return `${formattedHours}:${minutesStr}`;
    };

    useEffect(() => {
        if (shiftId) {
            fetchShiftData();
        }
    }, [shiftId]);

    const fetchShiftData = async () => {
        setLoading(true);
        try {
            const response = await getClientUrlDataToken(
                authState.apiUrl,
                authState.userRole === 1
                    ? `${getShiftDetails}?shift_id=${shiftId}`
                    : `${getShiftDetailsRole}?shift_id=${shiftId}`
            );
            if (response.status === true) {
                const shiftData = Array.isArray(response.data.data)
                    ? response.data.data[0]
                    : response.data.data;
                if (shiftData) {
                    setValue("scheduleName", shiftData.name || "");
                    setValue("startTime", convertTo24Hour(shiftData.start_time));
                    setValue("endTime", convertTo24Hour(shiftData.end_time));
                    setValue("description", shiftData.description || "");
                    setValue("status", { value: shiftData.status || "active", label: shiftData.status === "active" ? "Active" : "Inactive" });
                }
            } else {
                toast.error(response.data?.message || "Failed to load shift details");
            }
        } catch (error) {
            toast.error("An error occurred while loading shift details");
        }
        setLoading(false);
    };

    const formatTo12Hour = (time24) => {
        if (!time24) return "";
        const [hoursStr, minutesStr] = time24.split(":");
        let hours = parseInt(hoursStr, 10);
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        return `${formattedHours}:${minutesStr} ${ampm}`;
    };

    const onSubmit = async (data) => {
        setLoading(true);
        const postRequest = {
            description: data?.description,
            name: data.scheduleName,
            start_time: formatTo12Hour(data.startTime),
            end_time: formatTo12Hour(data.endTime),
        };
        if (isEdit) {
            postRequest.shift_id = shiftId;
        }

        const endpoint = isEdit
            ? (authState.userRole === 1 ? updateShift : updateShiftRole)
            : (authState.userRole === 1 ? storeShiftDetails : storeShiftDetailsRole);

        const addCustomerResponse = await postClientUrlWithToken(
            authState.apiUrl,
            endpoint,
            postRequest
        );

        if (addCustomerResponse.status === 200) {
            if (addCustomerResponse.data.status === true) {
                toast.success(addCustomerResponse.data.message);
                navigate(`${process.env.PUBLIC_URL}/attendance/shifts`);
            } else {
                addCustomerResponse.data.message.forEach((err) => {
                    toast.error(err);
                });
                setLoading(false);
            }
        } else {
            HandleApiError(navigate, addCustomerResponse.status);
        }
        setLoading(false);
    };

    return (
        <Fragment>
            {loading && <Transparentspinner />}
            <Breadcrumbs
                mainTitle={isEdit ? "Edit Shift" : "Add Shift"}
                parent="Attendance"
                title={isEdit ? "Edit Shift" : "Add Shift"}
            />
            <Card>
                <CardBody>
                    <H3>{isEdit ? "Edit Shift" : "Add Shift"}</H3>
                    <Form className="needs-validation" noValidate onSubmit={handleSubmit(onSubmit)}>
                        <Row className="g-3 pt-3 p-3">
                            <Col md="6">
                                <Label className="form-label">Schedule Name <span className="requireStar">*</span></Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Schedule Name"
                                    {...register("scheduleName")}
                                />
                                <p className="text-danger">{errors.scheduleName?.message}</p>
                            </Col>

                            <Col md="6">
                                <Label className="form-label">Start Time <span className="requireStar">*</span></Label>
                                <input
                                    type="time"
                                    className="form-control"
                                    {...register("startTime")}
                                />
                                <p className="text-danger">{errors.startTime?.message}</p>
                            </Col>

                            <Col md="6">
                                <Label className="form-label">End Time <span className="requireStar">*</span></Label>
                                <input
                                    type="time"
                                    className="form-control"
                                    {...register("endTime")}
                                />
                                <p className="text-danger">{errors.endTime?.message}</p>
                            </Col>

                            <Col md="6">
                                <Label className="form-label">Description </Label>
                                <textarea
                                    className="form-control"
                                    placeholder="Description"
                                    {...register("description")}
                                />
                            </Col>
                        </Row>

                        <FormGroup></FormGroup>
                        <div className="d-flex justify-content-end gap-2">
                            <Button className='btn btn-light' type="button" onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/shifts`)}>
                                {Cancel}
                            </Button>
                            <Button type="submit">
                                {Save}
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default AddShift;
